import React, { useState, useEffect, useCallback } from 'react';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import { Form, Button, Modal, Badge, Alert } from 'react-bootstrap';
import {
  FaUserPlus, FaEdit, FaCheck, FaTimes, FaClock,
  FaUsers, FaCalendarCheck, FaCalendarTimes, FaEnvelope, FaSpinner, FaDownload, FaQrcode, FaSearch
} from 'react-icons/fa';
import ExportUtility from '../../components/ExportUtility/ExportUtility';
import WaalikwaModal from '../../modals/waalikwa/WaalikwaModal/WaalikwaModal';
import ScanCardModal from '../../modals/ecards/ScanCardModal';
import { invitationApi } from '../../api/guestList/invitationApi';
import { useAuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import { Dropdown } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Waalikwa.css';
import apiClient from '../../api';
import { useDebounce } from '../../hooks/useDebounce';

/* ─── helpers ─────────────────────────────────────────────────────────────── */
const normaliseRsvp = (rsvp) => {
  if (rsvp === null || rsvp === undefined || rsvp === 0 || rsvp === '0') return 'pending';
  if (rsvp === 1 || rsvp === '1' || rsvp === 'nitaudhuria' || rsvp === 'Nitahudhuria') return 'nitaudhuria';
  if (rsvp === 2 || rsvp === '2' || rsvp === 'Sitohudhuria' || rsvp === 'sitohudhuria' || rsvp === 'Sitaweza Kuhudhuria') return 'Sitohudhuria';
  if (rsvp === 3 || rsvp === '3' || rsvp === 'Huenda Nikahudhuria' || rsvp === 'huenda' || rsvp === 'Huenda') return 'huenda';
  return 'pending';
};

/* ─── component ───────────────────────────────────────────────────────────── */

const Waalikwa = () => {
  const { isAuthenticated, currentEvent } = useAuthContext();

  const [activeCard, setActiveCard] = useState('all');
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showScanCardModal, setShowScanCardModal] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState(null);
  const [mainTab, setMainTab] = useState('invitations');
  const [isExporting, setIsExporting] = useState(false);

  const [visitorsData, setVisitorsData] = useState([]);
  const [stats, setStats] = useState({
    totalInvitations: 0,
    totalGuests: 0,
    nitaudhuria: 0,
    Sitohudhuria: 0,
    pending: 0
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  /* ── fetch with pagination ── */
  const fetchWaalikwa = useCallback(async (p = 1, pp = 10, search = '') => {
    setLoading(true);
    setError(null);
    try {
      let response;
      const params = {
        page: p,
        per_page: pp,
        search: search || undefined
      };
      if (mainTab === 'attendance') {
        response = await invitationApi.getAttendanceList(params);
      } else if (mainTab === 'thank_notes') {
        response = await invitationApi.getThankNoteList(params);
      } else {
        response = await invitationApi.getInvitedGuests(params);
      }

      // Extract API JSON body
      const apiResponse = response?.data ?? response ?? {};
      // Determine if apiResponse has nested data object for pagination metadata
      let paginationData = apiResponse;
      if (apiResponse?.success && apiResponse?.data && !Array.isArray(apiResponse?.data)) {
        paginationData = apiResponse.data;
      }

      // Extract the actual array of guests
      const raw = Array.isArray(paginationData) ? paginationData : (paginationData?.data ?? paginationData?.guests ?? []);

      const creditsVal = apiResponse?.credits ?? paginationData?.credits ?? null;
      const recordsTotal = paginationData?.total_records ?? paginationData?.total ?? raw?.length ?? 0;
      const totalPagesCount = paginationData?.total_pages ?? paginationData?.last_page ?? Math.ceil(recordsTotal / pp);

      // No need to filter by sent_status anymore since /guests/invited handles it
      const filteredRaw = Array.isArray(raw) ? raw : [];

      const formatted = filteredRaw.map((g, index) => ({
        id: g.guest_lists_id || g.guest_list_id || g.id || `${g.card_number || 'guest'}-${index}`,
        guest_lists_id: g.guest_lists_id || g.guest_list_id || g.id,
        guest_list_id: g.guest_lists_id || g.guest_list_id || g.id,
        name: g.name || g.full_name || '',
        phone: g.phone_number || g.mobile || '',
        cardNumber: g.card_number || '—',
        seats: Number(g.card_type) === 2 || g.card_type === '2' || g.card_type === 'Double' ? 2 : 1,
        sendStatus: g.send_status,
        sentVia: g.sent_via,
        generatedStatus: g.generated_status,
        cardType: (g.card_type == 1 || g.card_type === '1') ? 'Single' :
          (g.card_type == 2 || g.card_type === '2') ? 'Double' :
            (g.card_type || 'Single'),
        pledge: g.pledge || 0,
        fund: g.fund || 0,
        status: normaliseRsvp(g.rsvp),
        attended: Number(g.attended) === 1 || Number(g.attendance_status) === 1 || Number(g.is_attended) === 1 || g.attended === true ? 1 : 0,
        dateResponded: g.updated_at ? g.updated_at.split(' ')[0] : null,
      }));

      setVisitorsData(formatted);
      setTotalRecords(recordsTotal);
      setTotalPages(totalPagesCount);
      setCredits(creditsVal);

      // Calculate stats locally from the fetched page's data
      let nitaudhuria = 0;
      let Sitohudhuria = 0;
      let huenda = 0;
      let pending = 0;

      formatted.forEach(g => {
        const rsvpStatus = g.status;
        if (rsvpStatus === 'nitaudhuria') {
          nitaudhuria += 1;
        } else if (rsvpStatus === 'Sitohudhuria') {
          Sitohudhuria += 1;
        } else if (rsvpStatus === 'huenda') {
          huenda += 1;
        } else {
          pending += 1;
        }
      });

      setStats({
        totalInvitations: recordsTotal,
        totalGuests: recordsTotal,
        nitaudhuria,
        Sitohudhuria,
        huenda,
        pending
      });
    } catch (err) {
      console.error('Error fetching waalikwa:', err);
      setError('Imeshindwa kupakia data. Jaribu tena.');
    } finally {
      setLoading(false);
    }
  }, [mainTab]);

  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      if (debouncedSearchTerm === '' || debouncedSearchTerm.length >= 3) {
        fetchWaalikwa(page, perPage, debouncedSearchTerm);
      }
    }
  }, [isAuthenticated, currentEvent, page, perPage, mainTab, debouncedSearchTerm]);

  /* ── pagination handlers ── */
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const handleRefresh = () => {
    fetchWaalikwa(page, perPage, searchTerm);
  };

  /* ── PDF & Export ── */
  const exportColumns = [
    { header: 'Na', dataKey: 'index' },
    { header: 'Jina', dataKey: 'name' },
    { header: 'Namba ya Simu', dataKey: 'phone' },
    { header: 'Kadi', dataKey: 'cardNumber' },
    { header: 'Aina ya Kadi', dataKey: 'cardType' },
    { header: 'Mahudhurio', dataKey: 'status' }
  ];

  const fetchExportData = async () => {
    try {
      const response = await apiClient.get('/export/invitations/json');
      const dataPayload = response?.data;

      const eventTitle = dataPayload?.event || currentEvent?.event_title || 'Event';
      const today = new Date();
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
      const dd = today.getDate();
      const monthText = monthNames[today.getMonth()];
      const yyyy = today.getFullYear();
      const formattedDate = `${dd} ${monthText} ${yyyy}`;
      const generatedTitle = `${eventTitle} Invitations report printed on ${formattedDate}`;

      const rawData = dataPayload?.invitations || dataPayload?.guests || dataPayload?.data || response?.data || response || [];
      const invitationsList = Array.isArray(rawData) ? rawData : (Array.isArray(rawData?.invitations) ? rawData.invitations : []);

      const data = invitationsList.map((item, index) => {
        const cardTypeLabel = (item.card_type == 1 || item.card_type === '1' || item.card_type === 'Single') ? 'Single' :
          (item.card_type == 2 || item.card_type === '2' || item.card_type === 'Double') ? 'Double' : (item.card_type || 'Single');
        const isAttended = Number(item.attended) === 1 || Number(item.attendance_status) === 1 || Number(item.is_attended) === 1 || item.attended === true;

        return {
          index: index + 1,
          name: item.full_name || item.name || item.guest_name || 'N/A',
          phone: item.mobile || item.phone_number || item.phone || 'N/A',
          cardNumber: item.card_number || '—',
          cardType: cardTypeLabel.toUpperCase(),
          status: isAttended ? 'Amehudhuria' : ''
        };
      });

      return {
        data,
        pdfTitle: generatedTitle
      };
    } catch (err) {
      console.error('Error fetching attendance for export:', err);
      toast.error('Imeshindwa kupakua data ya wageni');
      return [];
    }
  };

  /* ── filter data based on active card, search, and status filter ── */
  const getFilteredData = () => {
    let filtered = visitorsData;

    // Filter by active card (stats card selection)
    if (activeCard === 'nitaudhuria') {
      filtered = filtered.filter(v => v.status === 'nitaudhuria');
    } else if (activeCard === 'Sitohudhuria') {
      filtered = filtered.filter(v => v.status === 'Sitohudhuria');
    } else if (activeCard === 'huenda') {
      filtered = filtered.filter(v => v.status === 'huenda');
    } else if (activeCard === 'pending') {
      filtered = filtered.filter(v => v.status === 'pending');
    } else if (activeCard === 'responded') {
      filtered = filtered.filter(v => v.status !== 'pending');
    }

    // Filter by status dropdown
    if (filterStatus !== 'all') {
      filtered = filtered.filter(v => v.status === filterStatus);
    }

    // Local Search Filter has been removed as it is handled by the server
    return filtered;
  };

  const filteredData = getFilteredData();

  const percentages = {
    nitaudhuria: stats.totalGuests > 0 ? Math.round((stats.nitaudhuria / stats.totalGuests) * 100) : 0,
    Sitohudhuria: stats.totalGuests > 0 ? Math.round((stats.Sitohudhuria / stats.totalGuests) * 100) : 0,
    huenda: stats.totalGuests > 0 ? Math.round(((stats.huenda || 0) / stats.totalGuests) * 100) : 0,
    pending: stats.totalGuests > 0 ? Math.round((stats.pending / stats.totalGuests) * 100) : 0,
    responseRate: stats.totalGuests > 0
      ? Math.round(((stats.nitaudhuria + stats.Sitohudhuria + (stats.huenda || 0)) / stats.totalGuests) * 100)
      : 0,
  };

  /* ── RSVP update ── */
  const handleOpenUpdateModal = (guest) => {
    setSelectedGuest(guest);
    setNewStatus(guest.status);
    setShowUpdateModal(true);
  };

  const handleUpdateStatus = async () => {
    try {
      let rsvpValue = 0;
      if (newStatus === 'nitaudhuria') rsvpValue = 1;
      else if (newStatus === 'Sitohudhuria') rsvpValue = 2;
      else if (newStatus === 'huenda') rsvpValue = 3;

      const guestListId = selectedGuest?.guest_lists_id || selectedGuest?.guest_list_id || selectedGuest?.id;
      const guestListIdNum = Number(guestListId) || guestListId;
      const eventId = currentEvent?.id || currentEvent?.events_id || selectedGuest?.event_id || selectedGuest?.events_id || null;

      const payload = {
        guest_id: guestListIdNum,
        rsvp: rsvpValue,
        event_id: eventId,
        submit: 'Hakiki'
      };

      const response = await invitationApi.recordRsvp(guestListIdNum, payload);

      handleApiResponse(response);
      setShowUpdateModal(false);
      fetchWaalikwa(page, perPage, searchTerm);
    } catch (err) {
      console.error('Error updating RSVP status:', err);
      handleApiResponse(err);
    }
  };

  const handleBackendExport = async (type = 'pdf') => {
    setIsExporting(true);
    try {
      const eventName = currentEvent?.event_title || 'Waalikwa';
      await invitationApi.exportInvitations(type, `${eventName}_Invitations`);
      toast.success('Download started!');
    } catch (err) {
      console.error(err);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  /* ── status badge cell ── */
  const statusCell = (row) => {
    const styles = {
      nitaudhuria: { color: '#15803d', border: '1.5px solid #22c55e', background: '#f0fdf4' },
      Sitohudhuria: { color: '#b91c1c', border: '1.5px solid #ef4444', background: '#fef2f2' },
      huenda: { color: '#b45309', border: '1.5px solid #f59e0b', background: '#fffbeb' },
      pending: { color: '#b45309', border: '1.5px solid #f59e0b', background: '#fffbeb' },
    };
    const labels = {
      nitaudhuria: 'Nitahudhuria',
      Sitohudhuria: 'Sitohudhuria',
      huenda: 'Huenda Nikahudhuria',
      pending: 'Pending',
    };
    const base = {
      borderRadius: '20px',
      padding: '0.25rem 0.85rem',
      fontSize: '0.82rem',
      fontWeight: '600',
      display: 'inline-block',
      whiteSpace: 'nowrap',
    };
    return (
      <span style={{ ...base, ...(styles[row.status] || styles.pending) }}>
        {labels[row.status] || 'Pending'}
      </span>
    );
  };

  /* ── sent-via badge ── */
  const sentViaCell = (row) => {
    if (row.sendStatus === 0) return <Badge bg="secondary">Haijatumwa</Badge>;
    if (row.sentVia === 1) return <Badge bg="primary">SMS</Badge>;
    if (row.sentVia === 2) return <Badge bg="success">WhatsApp</Badge>;
    return <Badge bg="info">Imetumwa</Badge>;
  };

  /* ── attendance badge cell ── */
  const attendanceCell = (row) => {
    if (row.attended === 1) {
      return (
        <span className="badge rounded-pill bg-success-subtle text-success border border-success-subtle px-2.5 py-1 fw-semibold" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          Amehudhuria
        </span>
      );
    }
    return (
      <span className="badge rounded-pill bg-warning-subtle text-dark border border-warning-subtle px-2.5 py-1 fw-semibold" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
        Pending
      </span>
    );
  };

  /* ── columns ── */
  const columns = [
    { name: '#', cell: (row, index) => (page - 1) * perPage + index + 1, width: '55px', center: true },
    { name: 'Jina', selector: r => r.name, sortable: true, grow: 2 },
    {
      name: 'No Ya Simu',
      selector: r => r.phone,
      sortable: true,
      cell: r => (
        <a href={`tel:${r.phone}`} style={{ textDecoration: 'none', color: '#4169e1' }}>
          {r.phone}
        </a>
      ),
    },
    {
      name: 'Kadi namba',
      selector: r => r.cardNumber,
      sortable: true,
      cell: r => <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{r.cardNumber}</span>,
      width: '115px',
    },
    {
      name: 'Card Type',
      selector: r => r.cardType,
      sortable: true,
      width: '105px',
    },
    {
      name: 'RSVP',
      selector: r => r.status,
      sortable: true,
      cell: statusCell,
      width: '165px',
    },
    {
      name: 'Mahudhurio',
      selector: r => r.attended,
      sortable: true,
      cell: attendanceCell,
      width: '145px',
      center: true,
    },
    {
      name: 'Actions',
      cell: row => (
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => handleOpenUpdateModal(row)}
          style={{ borderRadius: '8px', padding: '0.25rem 0.65rem', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
        >
          Badilisha
        </Button>
      ),
      width: '130px',
      center: true,
    },
  ];

  /* ── loading / error ── */
  if (loading && visitorsData.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <FaSpinner className="fa-spin" size={48} color="#4169e1" />
          <p className="mt-3">Inapakia wageni waalikwa...</p>
        </div>
      </div>
    );
  }

  /* ── render ── */
  return (
    <div className="container">
      <div className="row">
        <div className="container-fluid mt-4">
          <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>Waalikwa</h1>

          {/* Navigation Tabs */}


          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}{' '}
              <Button variant="link" size="sm" onClick={() => fetchWaalikwa(page, perPage)}>Jaribu tena</Button>
            </Alert>
          )}

          {/* Credits info */}
          {credits !== null && (
            <div className="text-center mb-2">
              <small className="text-muted">
                <FaEnvelope className="me-1" />
                SMS Credits zilizobaki: <strong>{credits}</strong>
              </small>
            </div>
          )}

          {/* Statistics Section */}
          <div className="dashboard row mt-4">
            <div className="dashboard col text-center">
              <div
                className={`dashboard bg-dash p-4 rounded-lg ${activeCard === 'all' ? 'card-active' : ''}`}
                onClick={() => setActiveCard('all')}
                style={{ cursor: 'pointer' }}
              >
                <div className="dashText">
                  <FaUsers className="mb-2" size={24} style={{ color: '#007bff' }} />
                  <div>Mialiko Yote</div>
                  <div className="dashboard-stats-number">
                    {stats.totalInvitations}
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard col text-center">
              <div
                className={`dashboard bg-dash p-4 rounded-lg ${activeCard === 'nitaudhuria' ? 'card-active' : ''}`}
                onClick={() => setActiveCard('nitaudhuria')}
                style={{ cursor: 'pointer' }}
              >
                <div className="dashText">
                  <FaCalendarCheck className="mb-2" size={24} style={{ color: '#28a745' }} />
                  <div>Nitaudhuria</div>
                  <div className="dashboard-stats-number">
                    {stats.nitaudhuria}{' '}
                    <small className="stats-percentage">({percentages.nitaudhuria}%)</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard col text-center">
              <div
                className={`dashboard bg-dash p-4 rounded-lg ${activeCard === 'Sitohudhuria' ? 'card-active' : ''}`}
                onClick={() => setActiveCard('Sitohudhuria')}
                style={{ cursor: 'pointer' }}
              >
                <div className="dashText">
                  <FaCalendarTimes className="mb-2" size={24} style={{ color: '#dc3545' }} />
                  <div>Sitohudhuria</div>
                  <div className="dashboard-stats-number">
                    {stats.Sitohudhuria}{' '}
                    <small className="stats-percentage">({percentages.Sitohudhuria}%)</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard col text-center">
              <div
                className={`dashboard bg-dash p-4 rounded-lg ${activeCard === 'pending' ? 'card-active' : ''}`}
                onClick={() => setActiveCard('pending')}
                style={{ cursor: 'pointer' }}
              >
                <div className="dashText">
                  <FaClock className="mb-2" size={24} style={{ color: '#ffc107' }} />
                  <div>Hawajajibu</div>
                  <div className="dashboard-stats-number">
                    {stats.pending}{' '}
                    <small className="stats-percentage">({percentages.pending}%)</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard col text-center">
              <div
                className="dashboard bg-dash action-card-btn p-3 rounded-lg h-100 d-flex flex-column align-items-center justify-content-center"
                onClick={() => setShowScanCardModal(true)}
                style={{
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                  border: '1px solid #818cf8'
                }}
              >
                <div className="dashText text-center" style={{ color: '#3730a3' }}>
                  <FaQrcode className="mb-2" size={24} />
                  <div className="fw-semibold" style={{ fontSize: '0.9rem', lineHeight: '1.25' }}>Hakiki Kadi</div>
                </div>
              </div>
            </div>
          </div>

          {/* Unified Search & Action Toolbar */}
          <div className="bg-white p-3 rounded-4 shadow-sm border my-4 d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-3">
            <div className="position-relative flex-grow-1" style={{ maxWidth: '420px' }}>
              <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" style={{ zIndex: 5 }} />
              <Form.Control
                type="text"
                placeholder="Tafuta kwa jina, simu au namba ya kadi..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="ps-5 shadow-none border"
                style={{ borderRadius: '25px', height: '42px', fontSize: '0.85rem', borderColor: '#e2e8f0' }}
              />
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap flex-sm-nowrap">
              <Button
                variant="outline-secondary"
                onClick={handleRefresh}
                className="rounded-pill px-3 py-2 fw-semibold border d-flex align-items-center justify-content-center"
                style={{ height: '42px', fontSize: '0.85rem' }}
              >
                Refresh
              </Button>
              <ExportUtility
                fetchData={fetchExportData}
                columns={exportColumns}
                filename={`${currentEvent?.event_title || 'Waalikwa'}_Invitations`}
                pdfTitle={`${currentEvent?.event_title || 'Event'} Confirmation Invitations report printed on ${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}`}
                orientation="landscape"
                pdfOnlyButton={true}
                pdfButtonText="Waalikwa PDF"
                btnClassName="btn btn-outline-danger rounded-pill px-3 py-2 fw-semibold border d-flex align-items-center gap-2"
                btnStyle={{ height: '42px', fontSize: '0.85rem', display: 'flex', alignItems: 'center' }}
              />
            </div>
          </div>


          {/* Data Table without Pagination */}
          <StyledDataTable
            columns={columns}
            data={filteredData}
            progressPending={loading}
            emptyMessage="Hakuna wageni waalikwa walioorodheshwa bado."
            pagination={true}
            paginationServer={true}
            paginationTotalRows={totalRecords}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            paginationDefaultPage={page}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerPageChange}
          />
        </div>
      </div>

      {/* Add Guest Modal */}
      <WaalikwaModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        guest={selectedGuest}
      />

      {/* Update RSVP Status Modal */}
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} centered contentClassName="rounded-4 shadow-lg border-0">
        <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
          <Modal.Title className="fw-bold fs-5 text-dark">Badilisha Hali ya RSVP</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-3">
          {selectedGuest && (
            <>
              <div className="guest-info mb-4 p-3 bg-light rounded-3 border border-light-subtle">
                <h6 className="fw-bold text-secondary mb-2" style={{ fontSize: '0.88rem' }}>Taarifa za Mgeni</h6>
                <p className="mb-1 text-dark" style={{ fontSize: '0.88rem' }}><strong>Jina:</strong> {selectedGuest.name}</p>
                <p className="mb-1 text-dark" style={{ fontSize: '0.88rem' }}><strong>Simu:</strong> {selectedGuest.phone}</p>
                <p className="mb-0 text-dark" style={{ fontSize: '0.88rem' }}><strong>Kadi:</strong> {selectedGuest.cardNumber} ({selectedGuest.seats} viti)</p>
              </div>
              <Form.Group>
                <Form.Label className="fw-bold mb-2 text-dark" style={{ fontSize: '0.9rem' }}>Chagua Hali Mpya</Form.Label>
                <div className="status-options d-flex flex-column flex-sm-row gap-2">
                  <Button
                    variant={newStatus === 'nitaudhuria' ? 'success' : 'outline-success'}
                    className="flex-fill py-2 px-2 rounded-3 fw-semibold d-flex flex-column flex-sm-row align-items-center justify-content-center gap-1.5"
                    onClick={() => setNewStatus('nitaudhuria')}
                    style={{ fontSize: '0.84rem' }}
                  >
                    <FaCheck />
                    <span>Nitahudhuria</span>
                  </Button>
                  <Button
                    variant={newStatus === 'Sitohudhuria' ? 'danger' : 'outline-danger'}
                    className="flex-fill py-2 px-2 rounded-3 fw-semibold d-flex flex-column flex-sm-row align-items-center justify-content-center gap-1.5"
                    onClick={() => setNewStatus('Sitohudhuria')}
                    style={{ fontSize: '0.84rem' }}
                  >
                    <FaTimes />
                    <span>Sitaweza Kuhudhuria</span>
                  </Button>
                  <Button
                    variant={newStatus === 'huenda' ? 'primary' : 'outline-primary'}
                    className="flex-fill py-2 px-2 rounded-3 fw-semibold d-flex flex-column flex-sm-row align-items-center justify-content-center gap-1.5"
                    onClick={() => setNewStatus('huenda')}
                    style={{ fontSize: '0.84rem' }}
                  >
                    <FaClock />
                    <span>Huenda Nikahudhuria</span>
                  </Button>
                </div>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 px-4 pb-4 pt-2 d-flex justify-content-end gap-2">
          <Button
            variant="secondary"
            className="rounded-pill px-4 py-2 fw-semibold"
            onClick={() => setShowUpdateModal(false)}
            style={{ fontSize: '0.88rem', background: '#6c757d', borderColor: '#6c757d' }}
          >
            Funga
          </Button>
          <Button
            variant="primary"
            className="rounded-pill px-4 py-2 fw-semibold"
            onClick={handleUpdateStatus}
            style={{ fontSize: '0.88rem', background: '#4f46e5', borderColor: '#4f46e5' }}
          >
            Hifadhi Mabadiliko
          </Button>
        </Modal.Footer>
      </Modal>

      <ScanCardModal
        show={showScanCardModal}
        onHide={() => setShowScanCardModal(false)}
        onAttendanceMarked={handleRefresh}
      />
    </div>
  );
};

export default Waalikwa;